import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { JsonPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { PeriodicStatisticsService } from './periodic-statistics.service';
import { PeriodicStatisticDTO, PeriodType } from 'src/app/base/models/models.swagger.generated';

const PERIOD_TYPE = PeriodType.Daily;
const PERIOD_LENGTH_IN_DAYS = 30;

@Component({
  selector: 'app-periodic-statistics',
  templateUrl: './periodic-statistics.component.html',
  styleUrls: ['./periodic-statistics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, JsonPipe, TranslatePipe],
  providers: [PeriodicStatisticsService],
})
export class PeriodicStatisticsComponent {

  private readonly periodicStatisticsService = inject(PeriodicStatisticsService);

  /** Bound to the input field. Only applied to the request once the user triggers a load. */
  public readonly vehicleId = signal<number | null>(1);

  private readonly requestedVehicleId = signal<number | null>(1);

  private readonly periodicStatistics = rxResource({
    params: () => this.requestedVehicleId() ?? undefined,
    stream: ({ params: vehicleId }) => {
      const toUtc = DateTime.utc();
      const fromUtc = toUtc.minus({ days: PERIOD_LENGTH_IN_DAYS });
      return this.periodicStatisticsService.getPeriodicStatistics(vehicleId, PERIOD_TYPE, fromUtc, toUtc);
    },
    defaultValue: [] as PeriodicStatisticDTO[],
  });

  public readonly isLoading = this.periodicStatistics.isLoading;

  public readonly statistics = computed(() => this.periodicStatistics.hasValue() ? this.periodicStatistics.value() : []);

  public onLoad(): void {
    this.requestedVehicleId.set(this.vehicleId());
    // Re-requests when the id did not change; a no-op while the id change is already loading.
    this.periodicStatistics.reload();
  }
}
